<template>
  <div>
    <h2>{{ $t('views.endusers.tenants.title') }}</h2>

    <b-table
             :busy="isLoading"
             :fields="tableFields"
             :items="mappedTenants"
             striped
             hover
             show-empty
             sort-icon-left>
      <template #table-busy>
        <div class="text-center">
          <loader type="dots" inline scale="4" />
          <div>{{ $t('components.table.busy') }}</div>
        </div>
      </template>

      <template #cell(tenantName)="data">
        {{ data.value }}
      </template>

      <template #cell(created)="data">
        <span v-if="data.value" v-b-tooltip.hover.left="$d(new Date(data.value), 'long')">
          <timeago :datetime="data.value" :converterOptions="{ format: 'Pp' }" auto-update />
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(lastPasswordReset)="data">
        <span v-if="data.value" v-b-tooltip.hover.left="$d(new Date(data.value), 'long')">
          <timeago :datetime="data.value" :converterOptions="{ format: 'Pp' }" auto-update />
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(updated)="data">
        <span v-if="data.value" v-b-tooltip.hover.left="$d(new Date(data.value), 'long')">
          <timeago :datetime="data.value" :converterOptions="{ format: 'Pp' }" auto-update />
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(hasTenantLogin)="data">
        <b-badge v-if="data.value" variant="success">{{ $t('views.endusers.tenants.table.value.hasTenantLoginYes') }}</b-badge>
        <b-badge v-else variant="secondary">{{ $t('views.endusers.tenants.table.value.hasTenantLoginNo') }}</b-badge>
      </template>

      <template #cell(emailConfirmed)="data">
        <span v-if="!data.item.hasTenantLogin">-</span>
        <b-badge v-else-if="data.value" variant="success">{{ $t('views.endusers.tenants.table.value.emailConfirmedYes') }}</b-badge>
        <b-badge v-else variant="secondary">{{ $t('views.endusers.tenants.table.value.emailConfirmedNo') }}</b-badge>
      </template>

      <template #cell(lockoutEndDate)="data">
        <span v-if="data.value && new Date(data.value) > new Date()" v-b-tooltip.hover.left="$d(new Date(data.value), 'long')">
          <timeago :datetime="data.value" :converterOptions="{ format: 'Pp' }" auto-update />
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(actions)="data">
        <div v-if="data.item.hasTenantLogin && data.item.endUserTenantLoginId" class="d-flex actions-buttons">
          <b-button
                    size="sm"
                    variant="warning"
                    class="mr-1"
                    :disabled="isActionLoading(data.item.endUserTenantLoginId, 'triggerPasswordreset')"
                    @click="triggerPasswordreset(data.item)">
            <b-icon icon="reset" />
            {{ $t('views.endusers.tenants.action.resetpassword') }}
          </b-button>
          <b-button
                    v-if="!data.item.emailConfirmed"
                    size="sm"
                    variant="warning"
                    class="mr-1"
                    :disabled="isActionLoading(data.item.endUserTenantLoginId, 'triggerEmailConfirmation')"
                    @click="triggerEmailConfirmation(data.item)">
            <b-icon icon="check" />
            {{ $t('views.endusers.tenants.action.setemailconfirmed') }}
          </b-button>
          <b-button
                    v-if="data.item.lockoutEndDate && new Date(data.item.lockoutEndDate) > new Date()"
                    size="sm"
                    variant="warning"
                    class="mr-1"
                    :disabled="isActionLoading(data.item.endUserTenantLoginId, 'triggerResetLockout')"
                    @click="triggerResetLockout(data.item)">
            <b-icon icon="reset" />
            {{ $t('views.endusers.tenants.action.resetlockout') }}
          </b-button>
        </div>
      </template>

      <template #empty>
        <div class="text-center">{{ $t('components.table.empty') }}</div>
      </template>
    </b-table>
  </div>
</template>

<script>
import Loader from '@/components/Loader';
import MixinEntityBase from '@/components/mixins/EntityBase';

export default {
  name: 'EndUsers.Tenants',
  mixins: [MixinEntityBase],
  components: {
    Loader
  },
  data() {
    return {
      tableFields: [
        {
          key: 'tenantName',
          label: this.$t('views.endusers.tenants.table.label.tenantName'),
          sortable: true
        },
        {
          key: 'hasTenantLogin',
          label: this.$t('views.endusers.tenants.table.label.hasTenantLogin'),
          sortable: true
        },
        {
          key: 'emailConfirmed',
          label: this.$t('views.endusers.tenants.table.label.emailConfirmed'),
          sortable: true
        },
        {
          key: 'lockoutEndDate',
          label: this.$t('views.endusers.tenants.table.label.lockoutEndDate'),
          sortable: true,
          class: 'cell-align-right'
        },
        {
          key: 'lastPasswordReset',
          label: this.$t('views.endusers.tenants.table.label.lastPasswordReset'),
          sortable: true,
          class: 'cell-align-right'
        },
        {
          key: 'created',
          label: this.$t('views.endusers.tenants.table.label.created'),
          sortable: true,
          class: 'cell-align-right'
        },
        {
          key: 'updated',
          label: this.$t('views.endusers.tenants.table.label.updated'),
          sortable: true,
          class: 'cell-align-right'
        },
        {
          key: 'actions',
          label: this.$t('views.endusers.tenants.table.label.actions'),
          sortable: false,
          class: 'cell-align-center actions-column',
          thStyle: { whiteSpace: 'nowrap' },
          tdClass: 'actions-cell'
        }
      ]
    }
  },
  computed: {
    tenantsIs() {
      return this.$store.getters['tenants/is']
    },
    tenantLoginsIs() {
      return this.$store.getters['endusertenantlogins/is']
    },
    isLoading() {
      return this.tenantsIs.loading || this.tenantLoginsIs.loading
    },
    tenants() {
      return this.$store.getters['tenants/getUnwrapped'] || []
    },
    tenantLogins() {
      return this.$store.getters['endusertenantlogins/getUnwrapped'] || []
    },
    mappedTenants() {
      // Create a map of tenantId -> tenant
      const tenantMap = {}
      this.tenants.forEach(tenant => {
        tenantMap[tenant.id] = tenant
      })

      // Map logins to tenants
      return this.tenantLogins
        .map(login => {
          const tenant = tenantMap[login.tenantId]
          // Check if tenantLoginId exists and is not null/undefined and not empty GUID
          // Try both camelCase and PascalCase property names
          const tenantLoginId = login.tenantLoginId || login.TenantLoginId
          const hasTenantLogin = tenantLoginId &&
            tenantLoginId !== '00000000-0000-0000-0000-000000000000'
          return {
            tenantName: tenant ? tenant.name : `Unknown (${login.tenantId})`,
            hasTenantLogin: hasTenantLogin,
            endUserTenantLoginId: login.id,
            emailConfirmed: login.emailConfirmed || login.EmailConfirmed || false,
            lockoutEndDate: login.lockoutEndDate || login.LockoutEndDate || null,
            lastPasswordReset: login.lastPasswordReset || login.LastPasswordReset || null,
            created: login.created,
            updated: login.updated
          }
        })
        .sort((a, b) => {
          // Sort by tenant name
          return a.tenantName.localeCompare(b.tenantName)
        })
    }
  },
  methods: {
    getTenants() {
      this.$store.dispatch('tenants/getAll')
    },
    getTenantLogins() {
      this.$store.dispatch('endusertenantlogins/getByEndUser', this.eParent.id)
    },
    async triggerPasswordreset(item) {
      if (!item.endUserTenantLoginId) return
      await this.$store.dispatch('endusertenantlogins/triggerPasswordreset', item.endUserTenantLoginId)
      await this.getTenantLogins()
    },
    async triggerEmailConfirmation(item) {
      if (!item.endUserTenantLoginId) return
      await this.$store.dispatch('endusertenantlogins/triggerEmailConfirmation', item.endUserTenantLoginId)
      await this.getTenantLogins()
    },
    async triggerResetLockout(item) {
      if (!item.endUserTenantLoginId) return
      await this.$store.dispatch('endusertenantlogins/triggerResetLockout', item.endUserTenantLoginId)
      await this.getTenantLogins()
    },
    isActionLoading(endUserTenantLoginId, actionKey) {
      const is = this.$store.getters['endusertenantlogins/is']
      return is.sending && is.sending[actionKey] === true
    }
  },
  created() {
    this.getTenants()
    this.getTenantLogins()
  }
}
</script>

<style lang="scss">
::v-deep .actions-column {
  white-space: nowrap;

  th,
  td {
    white-space: nowrap;
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
}

.actions-cell {
  white-space: nowrap;
  width: 1% !important;
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
}

.actions-buttons {
  width: fit-content;
  margin: 0;
  flex-wrap: nowrap !important;

  .btn {
    white-space: nowrap !important;
    flex-shrink: 0;
  }
}
</style>
